import React from 'react';
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { TestimonialsSection } from "../components/testimonials/TestimonialsSection";
import { PricingTiers } from "../components/subscription/PricingTiers";
import { Compass, BookOpen, Users, Target, Award, TrendingUp, ArrowRight } from "lucide-react";
import Button from "../components/ui/Button";
import { useAuth } from '../contexts/AuthContext';
import CareerQuestionnaireModal from '../components/questionnaire/CareerQuestionnaireModal';
import { useQuestionnaire } from '../components/questionnaire/useQuestionnaire';

const Landing = () => {
  const navigate = useNavigate();
  const { isAuthenticated, openAuthModal } = useAuth();
  const { 
    isModalOpen, 
    setIsModalOpen, 
    handleStartQuestionnaire, 
    handleQuestionnaireComplete 
  } = useQuestionnaire();

  const features = [
    {
      icon: <Compass className="w-6 h-6 text-primary-600" />,
      title: "Career Path Discovery",
      description: "Explore personalized career paths based on your skills, interests, and goals."
    },
    {
      icon: <BookOpen className="w-6 h-6 text-primary-600" />,
      title: "Skill Development",
      description: "Access curated learning resources and track your progress with our skill development tools."
    },
    {
      icon: <Users className="w-6 h-6 text-primary-600" />,
      title: "Mentorship",
      description: "Connect with industry experts for guidance and support in your career journey."
    },
    {
      icon: <Target className="w-6 h-6 text-primary-600" />,
      title: "Goal Tracking",
      description: "Set and track career goals with our interactive dashboard and progress metrics."
    },
    {
      icon: <Award className="w-6 h-6 text-primary-600" />,
      title: "Certification Paths",
      description: "Discover relevant certifications and create structured learning paths."
    },
    {
      icon: <TrendingUp className="w-6 h-6 text-primary-600" />,
      title: "Market Insights",
      description: "Stay informed about industry trends and job market opportunities."
    }
  ];

  const steps = [
    {
      number: "01",
      title: "Take the Assessment",
      description: "Complete our comprehensive career assessment to identify your strengths and interests."
    },
    {
      number: "02",
      title: "Explore Paths",
      description: "Discover and compare career paths that match your profile and aspirations."
    },
    {
      number: "03",
      title: "Build Your Plan",
      description: "Create a personalized roadmap with clear steps to achieve your career goals."
    },
    {
      number: "04",
      title: "Take Action",
      description: "Access resources, connect with mentors, and start your journey to success."
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-gradient-to-b from-primary-50 to-white">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -left-40 w-80 h-80 bg-primary-200 rounded-full mix-blend-multiply opacity-70 animate-blob"></div>
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-secondary-200 rounded-full mix-blend-multiply opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute top-40 left-1/2 transform -translate-x-1/2 w-80 h-80 bg-accent-200 rounded-full mix-blend-multiply opacity-70 animate-blob animation-delay-4000"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h1 className="text-5xl md:text-7xl font-bold text-gray-900 text-center">
                Navigate Your Future Career Path
              </h1>
              <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto text-center">
                Discover personalized education and career recommendations tailored to your goals, 
                interests, and aspirations.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex flex-col sm:flex-row gap-4 justify-center mt-8"
            >
              <Button 
                size="lg"
                className="text-lg px-8 py-4 bg-primary-600 text-white hover:bg-primary-700"
                icon={<ArrowRight className="h-5 w-5" />}
                iconPosition="right"
                onClick={handleStartQuestionnaire}
              >
                Get Started
              </Button>
              <Button 
                variant="outline"
                size="lg"
                className="text-lg px-8 py-4"
                onClick={() => navigate('/about')}
              >
                Learn More
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-wrap justify-center gap-6 mt-12"
            >
              <div className="flex items-center bg-white shadow-xl rounded-full px-8 py-4">
                <Users className="h-6 w-6 text-primary-600 mr-3" />
                <span className="text-2xl font-bold text-gray-900">50,000+ Users</span>
              </div>
              <div className="flex items-center bg-white shadow-xl rounded-full px-8 py-4">
                <Target className="h-6 w-6 text-primary-600 mr-3" />
                <span className="text-2xl font-bold text-gray-900">95% Success Rate</span>
              </div>
              <div className="flex items-center bg-white shadow-xl rounded-full px-8 py-4">
                <Award className="h-6 w-6 text-primary-600 mr-3" />
                <span className="text-2xl font-bold text-gray-900">Expert Guidance</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">How It Works</h2>
              <p className="text-xl text-gray-600 text-center">
                Your journey to career success in four simple steps
              </p>
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative flex flex-col items-center text-center"
                >
                  <div className="bg-primary-50 rounded-2xl p-8 h-full w-full flex flex-col items-center justify-center">
                    <span className="text-4xl font-bold text-primary-200 mb-4 block">
                      {step.number}
                    </span>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 text-center">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">
                Comprehensive Career Guidance
              </h2>
              <p className="text-xl text-gray-600 text-center">
                Tools and features to support your career development journey
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <div className="bg-primary-100 p-3 rounded-lg w-fit mb-6">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <TestimonialsSection />
      <PricingTiers />
      
      {/* Modified CTA Section */}
      <section className="py-20 bg-primary-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex flex-col items-center"
            >
              <h2 className="text-4xl font-bold mb-6 text-center">Ready to Start Your Career Journey?</h2>
              <p className="text-xl mb-8 text-primary-100 max-w-2xl text-center">
                Join thousands of professionals who have already transformed their careers with our platform.
              </p>
              <Button
                className="bg-primary-600 text-white hover:bg-primary-700 px-8 py-4 rounded-full font-semibold text-lg transition-colors duration-300 flex items-center gap-2"
                onClick={handleStartQuestionnaire}
                icon={<ArrowRight className="h-5 w-5" />}
                iconPosition="right"
              >
                Get Started Now
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Career Questionnaire Modal */}
      <CareerQuestionnaireModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onComplete={handleQuestionnaireComplete}
      />
    </div>
  );
};

export default Landing;